import { Can } from '@/components/can/Can';
import CrumbTitle from '@/components/crumbTitle/CrumbTitle';
import { Button } from '@/design-system/button';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { AssignCommissionForm } from '@/features/configuration-management/commision-management/AssignCommissionForm';
import { useCommissionsList } from '@/features/configuration-management/commision-management/hooks/useCommissionList';
import { CommissionsTable } from '@/features/configuration-management/commision-management/table';
import { PercentageIcon } from '@/icons';
import { createFileRoute, SearchSchemaInput } from '@tanstack/react-router';
import { z } from 'zod';

const sortItemSchema = z.object({
  id: z.enum(['categoryName', 'description', 'createdBy', 'createdDate', 'status']),
  desc: z.boolean(),
});

const CommissionsSearchSchema = z
  .object({
    sort: z
      .preprocess((val) => {
        if (typeof val === 'string') {
          try {
            return JSON.parse(val);
          } catch {
            return undefined;
          }
        }
        return val;
      }, z.array(sortItemSchema))
      .optional(),

    createdDate: z.string().optional(),

    q: z
      .any()
      .optional()
      .transform((val) => (val == null ? undefined : String(val))),

    status: z
      .string()
      .transform((val) => val.split(','))
      .pipe(z.array(z.enum(['A', 'B'])))
      .optional(),

    page: z.coerce.number().positive().catch(0),
    fetch: z.coerce.number().positive().max(100).catch(10),
  })
  .passthrough();

export const Route = createFileRoute('/(app)/configuration-management/commissions/')({
  loader: ({ context }) => {
    const {
      dialogFormStore: { onOpen },
    } = context;

    const renderActions = (
      <Can rule={{ allOf: [PERMS.commission.category.assign] }}>
        <div>
          <Button
            variant="outline"
            className="border-foreground"
            onClick={() => {
              onOpen({
                modalTitle: 'configurationManagement.commissions.page.buttonTitle.assignCommission',
                title: 'Assign New Role',
                okText: <CrumbTitle id="common.page.modal.submit" />,
                size: 'lg',
                component: <AssignCommissionForm />,
              });
            }}
          >
            <PercentageIcon />
            <CrumbTitle id="configurationManagement.commissions.page.buttonTitle.assignCommission" />
          </Button>
        </div>
      </Can>
    );
    return {
      actions: { renderActions },
    };
  },
  component: RouteComponent,
  validateSearch: (search: SearchSchemaInput) => CommissionsSearchSchema.parse(search),
});

function RouteComponent() {
  const { commissionsList, meta, isLoading } = useCommissionsList();
  return <CommissionsTable data={commissionsList} pagination={meta} isLoading={isLoading} />;
}
